import { ApolloLink, FetchResult, Observable } from '@apollo/client'
import { trace, SpanStatusCode } from '@opentelemetry/api'
import { OperationDefinitionNode } from 'graphql'

export const createOpenTelemetryLink = (): ApolloLink =>
  new ApolloLink((operation, forward) => {
    if (typeof window !== 'undefined') {
      return forward(operation)
    }

    const tracer = trace.getTracer('core/graphql')
    const operationName = operation.operationName || 'GraphQLOperation'
    const operationDefinition = operation.query.definitions.find(
      (def): def is OperationDefinitionNode =>
        def.kind === 'OperationDefinition',
    )

    // istanbul ignore next
    const operationType = operationDefinition?.operation || 'unknown'

    return new Observable<FetchResult>(observer => {
      let subscriptionCleanup: (() => void) | undefined

      tracer.startActiveSpan(
        `${operationType}.${operationName}`,
        {
          attributes: {
            'graphql.operation.name': operationName,
            'graphql.operation.type': operationType,
            'graphql.variables': JSON.stringify(operation.variables),
          },
        },
        span => {
          const subscription = forward(operation).subscribe({
            next: result => {
              if (result?.errors && result.errors.length > 0) {
                span.setStatus({
                  code: SpanStatusCode.ERROR,
                  message: result.errors[0].message,
                })
                span.recordException(new Error(result.errors[0].message))
              }
              observer.next(result)
            },
            error: error => {
              span.setStatus({
                code: SpanStatusCode.ERROR,
                message:
                  // istanbul ignore next
                  error instanceof Error ? error.message : 'Unknown error',
              })
              span.recordException(error)
              span.end()
              observer.error(error)
            },
            complete: () => {
              span.end()
              observer.complete()
            },
          })

          subscriptionCleanup = () => subscription.unsubscribe()
        },
      )

      return () => {
        subscriptionCleanup?.()
      }
    })
  })
